import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { useLocation } from 'wouter';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@nextui-org/react';
import useUserStore from 'common/src/stores/useUserStore';
import useDataStore from 'common/src/stores/useDataStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import { Alert } from './ui';
import useWarningsStore from '../store/warningsStore';
import useExporterLabelsStore from '../store/useExporterLabelsStore';

interface SettlementWarningsModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

const SettlementWarningsModal = ({
  isOpen,
  onOpenChange,
}: SettlementWarningsModalProps) => {
  const {
    codesMissingMapping,
    settlementLabelsMissingInMappings,
    settlementVarietiesOutsideSpecies,
    unclassifiedExporterCostsCount,
  } = useWarningsStore();
  const { userProfile } = useUserStore();
  const { selectedSpecies } = useDataStore();
  const { selectedSeason } = useFilterStore();
  const {
    labels,
    loading: labelsLoading,
    fetchLabels,
  } = useExporterLabelsStore();
  const [location, setLocation] = useLocation();
  const [isCodesInfoOpen, setIsCodesInfoOpen] = useState(false);
  const [isLabelsInfoOpen, setIsLabelsInfoOpen] = useState(false);
  const [isOfficialLabelsInfoOpen, setIsOfficialLabelsInfoOpen] =
    useState(false);

  const canLoadLabels = useMemo(
    () =>
      Boolean(
        userProfile?.exporter_id && selectedSpecies?.id && selectedSeason
      ),
    [userProfile?.exporter_id, selectedSeason, selectedSpecies?.id]
  );

  useEffect(() => {
    if (!canLoadLabels || !userProfile?.exporter_id || !selectedSpecies?.id) {
      return;
    }
    fetchLabels(
      userProfile.exporter_id,
      selectedSpecies.id,
      selectedSeason
    ).catch(() => undefined);
  }, [
    canLoadLabels,
    fetchLabels,
    selectedSeason,
    selectedSpecies?.id,
    userProfile?.exporter_id,
  ]);

  const hasNoDeclaredLabels =
    canLoadLabels && !labelsLoading && labels.length === 0;

  const goToSettingsHash = (hash: string) => {
    if (location.startsWith('/settings')) {
      if (window.location.hash !== hash) {
        window.location.hash = hash;
      } else {
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      }
      return;
    }
    setLocation(`/settings${hash}`);
  };

  const closeAndGo = (hash: string) => {
    onOpenChange(false);
    goToSettingsHash(hash);
  };

  const warnings = [
    {
      key: 'missing-official-labels',
      active: hasNoDeclaredLabels,
      title: 'Etiquetas oficiales pendientes',
      description:
        'Aún no has declarado tus etiquetas oficiales para esta especie y temporada.',
      actions: [
        {
          label: 'Declarar etiquetas',
          onClick: () => closeAndGo('#labels'),
          variant: 'primary',
        },
        {
          label: '¿Para qué?',
          onClick: () => setIsOfficialLabelsInfoOpen(true),
          variant: 'secondary',
        },
      ],
    },
    {
      key: 'labels-outside-officials',
      active: settlementLabelsMissingInMappings.length > 0,
      title: 'Etiquetas en liquidaciones fuera de etiquetas oficiales',
      description: `${settlementLabelsMissingInMappings.length} etiquetas de las liquidaciones no existen en las etiquetas oficiales. Estandarízalas para que tus comparativos y análisis sean más precisos.`,
      actions: [
        {
          label: 'Estandarizar etiquetas',
          onClick: () => closeAndGo('#migrations'),
          variant: 'primary',
        },
        {
          label: '¿Para qué?',
          onClick: () => setIsLabelsInfoOpen(true),
          variant: 'secondary',
        },
      ],
    },
    {
      key: 'varieties-outside-officials',
      active: settlementVarietiesOutsideSpecies.length > 0,
      title: 'Variedades fuera de variedades oficiales',
      description: `${settlementVarietiesOutsideSpecies.length} variedades en las liquidaciones no existen en las variedades oficiales de la especie.`,
      actions: [
        {
          label: 'Estandarizar datos',
          onClick: () => closeAndGo('#migrations'),
          variant: 'primary',
        },
      ],
    },
    {
      key: 'unclassified-exporter-costs',
      active: unclassifiedExporterCostsCount > 0,
      title: 'Costos sin categoría propia',
      description: `${unclassifiedExporterCostsCount} conceptos de costos de tus clientes no tienen una de tus categorías de costos asignada.`,
      actions: [
        {
          label: 'Asignar categorías',
          onClick: () => closeAndGo('#cost-categories'),
          variant: 'primary',
        },
      ],
    },
    {
      key: 'codes-missing-mapping',
      active: codesMissingMapping.length > 0,
      title: 'Códigos de embalaje sin formato',
      description: `${codesMissingMapping.length} códigos de embalaje de la Base de Despachos no tienen formato asignado.`,
      actions: [
        {
          label: 'Abrir mapeo',
          onClick: () => closeAndGo('#packing-codes-mapping'),
          variant: 'primary',
        },
        {
          label: '¿Para qué?',
          onClick: () => setIsCodesInfoOpen(true),
          variant: 'secondary',
        },
      ],
    },
  ];

  const activeWarnings = warnings.filter((warning) => warning.active);

  return (
    <>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange} size='lg'>
        <ModalContent>
          <ModalHeader>Alertas activas</ModalHeader>
          <ModalBody>
            {activeWarnings.length === 0 ? (
              <p className='text-sm text-default-500'>
                No hay alertas activas.
              </p>
            ) : (
              <div className='space-y-3'>
                {activeWarnings.map((warning) => (
                  <Alert key={warning.key} variant='danger'>
                    <div className='flex flex-col gap-3 md:flex-row md:items-start md:justify-between'>
                      <div className='flex items-start gap-3'>
                        <span className='inline-flex h-8 w-8 items-center justify-center rounded-full bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300'>
                          <Icon icon='tabler:info-circle' className='flex-shrink-0' width={16} />
                        </span>
                        <div>
                          <div className='text-sm font-semibold text-red-800 dark:text-red-200'>
                            {warning.title}
                          </div>
                          <div className='text-sm text-red-700 dark:text-red-300'>
                            {warning.description}
                          </div>
                        </div>
                      </div>
                      <div className='flex flex-wrap items-center gap-2 md:justify-end'>
                        {warning.actions.map((action) => (
                          <button
                            key={action.label}
                            type='button'
                            onClick={action.onClick}
                            className={
                              action.variant === 'secondary'
                                ? 'text-sm font-medium text-red-700 dark:text-red-300 border border-red-200 dark:border-red-700 px-3 py-1.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900'
                                : 'text-sm font-medium text-white bg-red-600 dark:bg-red-700 px-3 py-1.5 rounded-full hover:bg-red-700 dark:hover:bg-red-600'
                            }
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </Alert>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => onOpenChange(false)}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isCodesInfoOpen} onOpenChange={setIsCodesInfoOpen}>
        <ModalContent>
          <ModalHeader>¿Para qué sirve este mapeo?</ModalHeader>
          <ModalBody>
            <p>
              Asignar un formato a cada código de embalaje evita inconsistencias
              (por ejemplo, formatos duplicados con nombres distintos) y mejora
              la validación de tus liquidaciones.
            </p>
            <p>
              En otras palabras, con esto estarás indicando a la plataforma
              cuáles son tus formatos. Y luego, si la base de liquidaciones no
              cumple con dichos valores, te alertaremos para que lo puedas
              arreglar.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setIsCodesInfoOpen(false)}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal isOpen={isLabelsInfoOpen} onOpenChange={setIsLabelsInfoOpen}>
        <ModalContent>
          <ModalHeader>¿Para qué sirve estandarizar etiquetas?</ModalHeader>
          <ModalBody>
            <p>
              Estandarizar etiquetas mantiene consistencia dentro de tus
              liquidaciones haciendo más precisos tus comparativos y análisis.
            </p>
            <p>
              Por ejemplo si tienes una etiqueta &quot;GOLD&quot; y un cliente
              te la informa como &quot;GOLD LABEL&quot;, y se sube como tal sin
              estandarizar, la plataforma las trataría como diferentes y eso
              afectaría cualquier algoritmo que dependa de ellas, como pueden
              ser los Comparativos.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setIsLabelsInfoOpen(false)}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Modal
        isOpen={isOfficialLabelsInfoOpen}
        onOpenChange={setIsOfficialLabelsInfoOpen}
      >
        <ModalContent>
          <ModalHeader>¿Para qué sirven las etiquetas oficiales?</ModalHeader>
          <ModalBody>
            <p>
              Esto permitirá asegurar la estandarización de tu base de
              liquidaciones y crear comparativos industria por categoría de
              etiqueta PREMIUM, ESTANDAR y REGULAR para un análisis más
              profundo.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant='light' onPress={() => setIsOfficialLabelsInfoOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default SettlementWarningsModal;
